import * as ENGINE from '@gnsx/genesys.js';
import { PROJECT_PATH_PREFIX } from '@gnsx/genesys.js';
import { flow, getRoot, getSnapshot, t } from 'mobx-state-tree';

import type { AssetType } from '@/editor/core/assetTypes';
import type { IEditorStore } from '@/editor/store/editorStore';
import type { Instance } from 'mobx-state-tree';

import { PROJECT_ASSETS_FOLDER } from '@/const';
import { getClassInfos, registerGameClasses } from '@/editor/core/classHelpers';
import { getResourceClassInfos } from '@/editor/core/resourceHelpers';
import { AssetTree, type IAssetFile, VIRTUAL_PATH } from '@/editor/store/models/assetTree';
import { ClassInfo, type IClassInfo } from '@/editor/store/models/classInfo';
import { IS_DEV } from '@/lib/utils';

const DEBUG_LOG = {
  setCurrentDirectory: true && IS_DEV,
  refreshClassInfos: true && IS_DEV,
  refreshClassInfosDetails: false && IS_DEV,
} as const;

/** Break strong refs to default instances before detaching `ClassInfo` from MST maps. */
function releaseAllDefaultObjects(map: { values(): IterableIterator<IClassInfo> }): void {
  for (const ci of map.values()) {
    ci.releaseDefaultObject();
  }
}

/** How `setCurrentDirectory` updates the folder navigation history stack */
export type FolderNavHistoryMode = 'push' | 'skip' | 'replace';

function normaliseFolderPathForHistory(path: string): string {
  if (!path || path === VIRTUAL_PATH.Root) return VIRTUAL_PATH.Root;
  return path;
}

function getThumbnailServiceFromRootStore(store: unknown): IEditorStore['thumbnailService'] {
  const rootStore = getRoot(store) as Partial<IEditorStore> | undefined;
  return rootStore?.thumbnailService ?? null;
}

export const DEFAULT_FOLDER_PATH = {
  prefabs: `${PROJECT_ASSETS_FOLDER}/prefabs`,
  materials: `${PROJECT_ASSETS_FOLDER}/materials`,
  textures: `${PROJECT_ASSETS_FOLDER}/textures`,
  vfx: `${PROJECT_ASSETS_FOLDER}/vfx`,
  models: `${PROJECT_ASSETS_FOLDER}/models`,
  scenes: `${PROJECT_ASSETS_FOLDER}/scenes`,
  src: `${PROJECT_PATH_PREFIX}/src`,
} as const;

/**
 * Assets Store
 */
export const AssetsStore = t.model('AssetsStore', {
  actorClassMap: t.map(ClassInfo),
  componentClassMap: t.map(ClassInfo),
  resourceClassMap: t.map(ClassInfo),
  prefabMap: t.map(t.frozen<ENGINE.SerializedData>()),
  assetTree: AssetTree,
  folderNavHistory: t.optional(t.array(t.string), []),
  folderNavIndex: t.optional(t.number, -1),
}).volatile(self => ({
  getCachedAssetSelectFiles: (() => {
    const cache = new Map<string, { version: number; files: IAssetFile[] }>();
    return (
      version: number,
      assetTypes: AssetType | AssetType[],
      resourceType?: string,
      prefabClass?: string,
    ): IAssetFile[] => {
      const normalizedTypes = Array.isArray(assetTypes) ? assetTypes.join(',') : assetTypes;
      const cacheKey = `${normalizedTypes}|${resourceType ?? ''}|${prefabClass ?? ''}`;
      const existing = cache.get(cacheKey);
      if (existing && existing.version === version) {
        return existing.files;
      }

      const files = self.assetTree.getAssetFiles(assetTypes, resourceType, prefabClass);
      cache.set(cacheKey, { version, files });
      return files;
    };
  })(),
})).views(self => ({
  get actorClasses(): IClassInfo[] { return Array.from(self.actorClassMap.values()); },
  get componentClasses(): IClassInfo[] { return Array.from(self.componentClassMap.values()); },
  get resourceClasses(): IClassInfo[] { return Array.from(self.resourceClassMap.values()); },
  get prefabDataCopy(): Record<string, ENGINE.SerializedData> { return structuredClone(getSnapshot(self.prefabMap)); },
  getPrefabClassName(prefabName: string): string | undefined {
    const prefabData = self.prefabMap.get(prefabName);
    if (!prefabData) {
      return undefined;
    }
    return ENGINE.WorldSerializer.readRootClassFromSerializedData(prefabData);
  },
  get currentDirectory(): string { return self.assetTree.currentFolderPath; },
  get currentPrefabDirectory(): string { return this.currentDirectory.startsWith(PROJECT_PATH_PREFIX) ? this.currentDirectory : DEFAULT_FOLDER_PATH.prefabs; },
  get currentProjectDirectory(): string | undefined { return this.currentDirectory.startsWith(PROJECT_PATH_PREFIX) ? this.currentDirectory : undefined; },
  get currentProjectAssetDirectory(): string | undefined {
    if (!this.currentDirectory.startsWith(PROJECT_PATH_PREFIX)) return undefined;
    return this.currentDirectory === PROJECT_PATH_PREFIX ? PROJECT_ASSETS_FOLDER : this.currentDirectory;
  },
  get isProjectDirectory(): boolean { return this.currentDirectory.startsWith(PROJECT_PATH_PREFIX); },
  get projectAssetDirectories(): IAssetFile[] { return self.assetTree.getItemsIn(PROJECT_ASSETS_FOLDER, true, item => item.isFolder); },
  getAssetSelectFiles(assetTypes: AssetType | AssetType[], resourceType?: string, prefabClass?: string): IAssetFile[] {
    return self.getCachedAssetSelectFiles(self.assetTree.assetSelectCacheVersion, assetTypes, resourceType, prefabClass);
  },
  getAssetSelectOptions(assetTypes: AssetType | AssetType[], resourceType?: string, prefabClass?: string): string[] {
    return this.getAssetSelectFiles(assetTypes, resourceType, prefabClass).map(file => file.path);
  },
  get allAssetSelectOptions(): string[] { return self.assetTree.allFiles.map(file => file.path); },
  get textureSelectOptions(): string[] { return self.assetTree.textureFiles.map(file => file.path); },
  get materialSelectOptions(): string[] { return self.assetTree.materialFiles.map(file => file.path); },
  get canNavigateFolderBack(): boolean { return self.folderNavIndex > 0; },
  get canNavigateFolderForward(): boolean {
    return self.folderNavIndex < self.folderNavHistory.length - 1;
  },
})).actions(self => ({
  _applyCurrentDirectory(directory: string, refresh: boolean, refreshRecursive: boolean) {
    if (DEBUG_LOG.setCurrentDirectory) console.log('[AssetsStore.setCurrentDirectory] directory:', directory);
    const oldDirectory = self.assetTree.currentFolderPath;
    self.assetTree.setCurrentFolder(directory);
    if (refresh) {
      self.assetTree.refreshCurrentDirectory(refreshRecursive);
    }

    if (oldDirectory !== directory) {
      this.onDirectoryChanged(directory);
    }
  },

  setCurrentDirectory(
    directory: string,
    refresh: boolean = false,
    refreshRecursive: boolean = false,
    historyMode: FolderNavHistoryMode = 'push',
  ) {
    const normalisedTarget = normaliseFolderPathForHistory(directory);
    const normalisedCurrent = normaliseFolderPathForHistory(self.assetTree.currentFolderPath);

    if (historyMode === 'skip') {
      this._applyCurrentDirectory(directory, refresh, refreshRecursive);
      return;
    }
    if (historyMode === 'replace') {
      self.folderNavHistory.replace([normalisedTarget]);
      self.folderNavIndex = 0;
      this._applyCurrentDirectory(directory, refresh, refreshRecursive);
      return;
    }

    if (normalisedTarget !== normalisedCurrent) {
      const truncated = self.folderNavHistory.slice(0, self.folderNavIndex + 1);
      self.folderNavHistory.replace(truncated);
      const lastAfterTruncate = truncated.length > 0 ? truncated[truncated.length - 1] : undefined;
      if (lastAfterTruncate !== normalisedTarget) {
        self.folderNavHistory.push(normalisedTarget);
      }
      self.folderNavIndex = self.folderNavHistory.length - 1;
    }

    this._applyCurrentDirectory(directory, refresh, refreshRecursive);
  },

  navigateFolderBack() {
    if (self.folderNavIndex <= 0) return;
    self.folderNavIndex -= 1;
    const path = self.folderNavHistory[self.folderNavIndex];
    this.setCurrentDirectory(path, false, false, 'skip');
  },

  navigateFolderForward() {
    if (self.folderNavIndex >= self.folderNavHistory.length - 1) return;
    self.folderNavIndex += 1;
    const path = self.folderNavHistory[self.folderNavIndex];
    this.setCurrentDirectory(path, false, false, 'skip');
  },

  onDirectoryChanged(directory: string) {
    // Get visible assets in the new directory
    const visibleAssets = self.assetTree.currentItems;

    // Notify thumbnail service to prioritize this directory
    const thumbnailService = getThumbnailServiceFromRootStore(self);
    if (thumbnailService && visibleAssets.length > 0) {
      // Use setTimeout to avoid blocking the directory change
      setTimeout(() => {
        thumbnailService.onDirectoryChanged(directory, visibleAssets);
      }, 100);
    }
  },

  /** Manually trigger thumbnail generation for current directory */
  generateThumbnailsForCurrentDirectory() {
    const visibleAssets = self.assetTree.currentItems;
    const currentDirectory = self.assetTree.currentFolderPath;

    const thumbnailService = getThumbnailServiceFromRootStore(self);
    if (thumbnailService && visibleAssets.length > 0) {
      console.log(`🖼️ Manually triggering thumbnail generation for ${visibleAssets.length} assets in "${currentDirectory}"`);

      // Queue all visible assets with high priority
      visibleAssets.forEach(asset => {
        if (thumbnailService.isSupported(asset.assetType)) {
          thumbnailService.queueThumbnailGeneration(asset, 15); // Very high priority for manual trigger
        }
      });
    }
  },

  /** Manually trigger thumbnail generation for all assets */
  generateAllThumbnails() {
    const allAssets = Array.from(self.assetTree.files.values());

    const thumbnailService = getThumbnailServiceFromRootStore(self);
    if (thumbnailService) {
      const supportedAssets = allAssets.filter(asset => thumbnailService.isSupported(asset.assetType));

      if (supportedAssets.length > 0) {
        console.log(`🖼️ Manually triggering thumbnail generation for ${supportedAssets.length} assets across entire project`);

        // Queue all supported assets with medium priority (lower than current directory)
        supportedAssets.forEach(asset => {
          thumbnailService.queueThumbnailGeneration(asset, 5); // Medium priority for bulk generation
        });
      }
    }
  },
  setPrefabData(data: Record<string, ENGINE.SerializedData>) {
    self.prefabMap.replace(structuredClone(data));
  },
  updatePrefabData(path: string, data: ENGINE.SerializedData) {
    const mapData = structuredClone(data) as ENGINE.SerializedData;
    self.prefabMap.set(path, mapData);
    // Keep loader cache mutable; MST `frozen` storage may freeze snapshots for map values.
    ENGINE.PrefabManager.setPrefabData(path, structuredClone(data) as ENGINE.SerializedData);
  },
  updatePrefabPath(oldPath: string, newPath: string) {
    const prefabData = self.prefabMap.get(oldPath);
    if (prefabData) {
      self.prefabMap.delete(oldPath);
      self.prefabMap.set(newPath, prefabData);
    }
  },
})).actions(self => ({
  refreshClassInfos: flow(function* _refreshClassInfos() {
    try {
      yield registerGameClasses();
    } catch (error) {
      console.error('Error registering game classes', error);
    }

    const classInfos = yield getClassInfos(false);
    const resourceInfos: IClassInfo[] = yield getResourceClassInfos(false);

    if (DEBUG_LOG.refreshClassInfos) {
      console.log('[AssetsStore.refreshClassInfos]', DEBUG_LOG.refreshClassInfosDetails ? { classInfos, resourceInfos } : 'Class infos refreshed');
    }

    // Each `ClassInfo` constructs a full default Actor/component tree in
    // `afterCreate`. Without releasing first, MST keeps the old defaults
    // reachable and component counts climb every refresh cycle.
    releaseAllDefaultObjects(self.resourceClassMap);
    releaseAllDefaultObjects(self.actorClassMap);
    releaseAllDefaultObjects(self.componentClassMap);

    self.resourceClassMap.clear();
    resourceInfos.forEach(info => self.resourceClassMap.put(info));

    self.actorClassMap.clear();
    self.componentClassMap.clear();

    Object.entries(classInfos).forEach(([type, infos]) => {
      (infos as IClassInfo[]).forEach(info => {
        if (type === 'ENGINE.Actor') self.actorClassMap.put(info);
        else self.componentClassMap.put(info);
      });
    });
  })
}));

export interface IAssetsStore extends Instance<typeof AssetsStore> { }
